<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { Button } from 'next/polaris/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'next/polaris/ui/tooltip';
import { Notification16 } from '@carbon/icons-vue';

// Tier 1 bell: a link to the notifications screen (the tier-2 "Notifications" item goes to the same route), with
// the unread count as a badge. It replaces the old panel button (woot's openNotificationPanel had no listener).
defineProps({
  // Phone sheet: no hover, so no tooltip.
  isMobile: { type: Boolean, default: false },
  // Collapsed rail (44px): the badge stays inside the button.
  compact: { type: Boolean, default: false },
});

const emit = defineEmits(['navigate']);

const { t } = useI18n();
const route = useRoute();
const { accountScopedRoute } = useAccount();
const notificationMetadata = useMapGetter('notifications/getMeta');

const unreadCount = computed(() => {
  const count = Number(notificationMetadata.value?.unreadCount) || 0;
  if (!count) return '';
  return count < 100 ? `${count}` : '99+';
});

const isActive = computed(() =>
  ['inbox_view', 'inbox_view_conversation'].includes(route.name)
);
const label = computed(() =>
  unreadCount.value
    ? `${t('SIDEBAR.NOTIFICATIONS')} (${unreadCount.value})`
    : t('SIDEBAR.NOTIFICATIONS')
);
</script>

<template>
  <Tooltip :disabled="isMobile">
    <TooltipTrigger as-child>
      <Button
        as-child
        variant="ghost"
        size="icon-sm"
        class="relative"
        :class="
          isActive
            ? 'bg-polaris-sidebar-accent text-polaris-sidebar-accent-foreground hover:bg-polaris-sidebar-accent'
            : ''
        "
      >
        <RouterLink
          :to="accountScopedRoute('inbox_view')"
          :aria-label="label"
          :aria-current="isActive ? 'page' : undefined"
          data-notification-bell
          @click="emit('navigate')"
        >
          <Notification16 aria-hidden="true" />
          <span
            v-if="unreadCount"
            aria-hidden="true"
            class="absolute grid place-items-center rounded-full bg-polaris-destructive font-medium leading-none text-polaris-destructive-foreground"
            :class="
              compact
                ? 'end-0 top-0 min-h-3.5 min-w-3.5 px-0.5 text-[9px]'
                : '-end-1.5 -top-1 min-h-4 min-w-4 px-1 text-[10px]'
            "
          >
            {{ unreadCount }}
          </span>
        </RouterLink>
      </Button>
    </TooltipTrigger>
    <TooltipContent side="right">
      {{ $t('SIDEBAR.NOTIFICATIONS') }}
    </TooltipContent>
  </Tooltip>
</template>
